/* THEODORE fit picker — behaviour for <theodore-fit-picker> (theme block
   theodore-fit-picker.liquid). A custom element, not an inline script, so
   the picker works wherever its markup lands: the PDP on parse, and the
   quick-add modal, whose DOMParser-fetched nodes never execute inline
   scripts. Loaded globally (defer) from scripts.liquid.

   THEODORE (bug #456, 15 Oct 2026): a garment with a "Made to measure" (MTM)
   Size value must be ordered as that variant under every fit except Standard
   size, and as the size picked under Standard size. reconcile() keeps the
   nested Size picker in that state: it checks the target radio and dispatches
   a bubbling change, Horizon's own pattern (quick-add.js #syncVariantSelection),
   so Horizon fetches the variant, morphs the picker and sets the form's id
   input itself. Nothing here writes the id input. A product without the MTM
   value (data-mtm-variant-id absent, no such value) is left exactly as before.
   Address rule, product page only: while Standard size is the chosen fit the
   URL carries fit=standard beside Horizon's ?variant=. A page that opens with
   no fit chosen on a real size (a ?variant= link) is switched to MTM, unless
   the URL carries fit=standard, which reopens Standard size on that size.
   Quick-add dialog, every fit-gated product (MTM or not): the dialog's product
   form is replaced by a fresh copy before the first reconcile, because a form
   the dialog reused from the previous product can be deaf to variant changes
   (see reviveDialogForm below). */
(() => {
  if (customElements.get('theodore-fit-picker')) return;

  let instanceCounter = 0;

  const MTM_VALUE = 'made to measure';
  const norm = (s) => String(s || '').trim().toLowerCase();
  // Passed to update() by the one deferred call made at connect.
  const AT_CONNECT = {};

  class TheodoreFitPicker extends HTMLElement {
    /** Set on connect; see checkFit. */
    #check = null;
    #cancelLookup = null;
    #resumeLookup = null;

    disconnectedCallback() {
      this.#cancelLookup?.();
    }

    /**
     * Whether the garment has a fit the cart can carry: a radio chosen and its
     * detail complete (a size, a checked code, a saved entry). Settles the
     * picker's line properties first, so a caller can read the form straight
     * after. With report, a missing fit is marked on the picker, which is
     * brought into view and focused.
     * @param {boolean} [report]
     */
    checkFit(report = false) {
      return this.#check ? this.#check(report) : true;
    }

    /**
     * True when a made-to-order garment's fit picker refuses this product
     * form's add: product-form.js (submit), its add-to-cart button (animation)
     * and the sticky bar ask before acting. A picker answers only for the form
     * its line properties are bound to, so a card's own quick-add form beside
     * a garment is never held up, and a garment without a picker (ready to
     * wear, shoes, the cummerbund, gift cards) never has one to ask.
     * @param {Element | null} productForm - a <product-form-component>
     * @param {boolean} [report]
     */
    static refuses(productForm, report = false) {
      // getAttribute: a product form's <input name="id"> shadows form.id.
      const formId = productForm?.querySelector('form')?.getAttribute('id');
      if (!formId) return false;
      const scope = productForm.closest('dialog, .shopify-section') || document;
      for (const picker of scope.querySelectorAll('theodore-fit-picker')) {
        if (picker.querySelector('.td-fit__prop')?.getAttribute('form') !== formId) continue;
        if (typeof picker.checkFit === 'function' && !picker.checkFit(report)) return true;
      }
      return false;
    }

    connectedCallback() {
      if (this.dataset.bound) {
        this.#resumeLookup?.();
        return;
      }
      this.dataset.bound = '1';

      const root = this;
      const q = (s) => root.querySelector(s);
      const propFit = q('.td-fit__prop');
      const propMachine = q('.td-fit__prop-machine');
      if (!propFit || !propMachine) return;
      const loggedIn = root.dataset.loggedIn === 'true';
      let profiles = [];

      // Radios must group per instance — the PDP and an open quick-add
      // modal can hold two pickers stamped from the same block id.
      const uid = 'td-fit-i' + ++instanceCounter;
      root.querySelectorAll('.td-fit__radio').forEach((r) => { r.name = uid; });
      // Markup rendered before the line existed (a cached page during a
      // publish) gets one, so a refused Add still says why.
      let note = q('.td-fit__error');
      if (!note) {
        note = document.createElement('p');
        note.className = 'td-fit__error';
        note.hidden = true;
        const label = q('.td-fit__label');
        if (label) label.after(note);
        else note = null;
      }
      if (note) note.id = uid + '-error';

      const fmtDate = (iso) => {
        if (typeof iso !== 'string' || !iso.trim()) return '';
        try {
          const date = new Date(iso);
          return Number.isFinite(date.getTime())
            ? date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
            : '';
        } catch { return ''; }
      };
      const dateNotRecorded = root.dataset.dateNotRecorded || 'Date not recorded';

      // THEODORE: (bug #456) a radio or <option> is Made to measure by its
      // variant id (emitted by the block) or by its value.
      const mtmId = root.dataset.mtmVariantId || '';
      const isMtm = (el) =>
        Boolean(el) && ((mtmId !== '' && el.dataset.variantId === mtmId) || norm(el.value) === MTM_VALUE);

      // The product's own Size option (Horizon's variant-picker) is a child
      // block of the standard row, so it is a descendant of this element and
      // its change events reach the listener below — which is what keeps the
      // docket in step with the size. Read it live; never cache it.
      // THEODORE: (bug #456) Made to measure is never a standard size, so it
      // reads as no size: "standard:Made to measure" can never be written.
      const readStandardSize = () => {
        const picker = root.querySelector('variant-picker');
        if (picker) {
          const checked = picker.querySelector('.variant-option input[type="radio"]:checked');
          if (checked) return isMtm(checked) ? '' : (checked.value || '').trim();
          const dropdown = picker.querySelector('.variant-option__select');
          if (dropdown) return isMtm(dropdown.selectedOptions[0]) ? '' : (dropdown.value || '').trim();
          // An option with a single value renders as text, with no input.
          const single = picker.querySelector('.variant-option__swatch-value');
          if (single) return norm(single.textContent) === MTM_VALUE ? '' : single.textContent.trim();
        }
        const marker = (q('.td-fit__variant-size')?.dataset.size || '').trim();
        if (marker && norm(marker) !== MTM_VALUE) return marker;
        const fallback = q('.td-fit__size-select');
        return fallback ? (fallback.value || '').trim() : '';
      };

      // THEODORE: (bug #456) the Size control that holds the MTM value: its
      // radios (button style) or its <select> (dropdown style, a guard: the
      // template uses buttons). null when the product has no MTM value, which
      // turns everything below off.
      const sizeControl = () => {
        const picker = root.querySelector('variant-picker');
        if (!picker) return null;
        const radios = [...picker.querySelectorAll('.variant-option input[type="radio"]')];
        const mtmRadio = radios.find(isMtm);
        if (mtmRadio) {
          const group = radios.filter((r) => r.name === mtmRadio.name);
          return {
            picker,
            select: null,
            mtm: mtmRadio,
            real: group.filter((r) => !isMtm(r)),
            current: group.find((r) => r.checked) || null,
          };
        }
        for (const select of picker.querySelectorAll('select.variant-option__select')) {
          const options = [...select.options];
          const mtmOption = options.find(isMtm);
          if (mtmOption) {
            return {
              picker,
              select,
              mtm: mtmOption,
              real: options.filter((o) => !isMtm(o)),
              current: select.selectedOptions[0] || null,
            };
          }
        }
        return null;
      };

      // The product page itself, not the quick-add dialog or a card: the only
      // place Horizon rewrites ?variant=, and so the only place the address
      // may carry the fit.
      // A detached picker (quick-add.js removes the previous product's before
      // each morph) has no dialog ancestor left to say where it was, so it is
      // never on the product page.
      const onProductPage = (ctl) =>
        root.isConnected &&
        ctl.picker.dataset.templateProductMatch === 'true' &&
        !root.closest('quick-add-dialog, dialog, product-card');

      // fit=standard while Standard size is the chosen fit, gone otherwise.
      // Written BEFORE the change is dispatched: Horizon builds its ?variant=
      // rewrite from the address as it stands then, so the marker survives it.
      const syncAddress = (ctl, standard) => {
        if (!onProductPage(ctl)) return;
        const url = new URL(window.location.href);
        if ((url.searchParams.get('fit') === 'standard') === standard) return;
        if (standard) url.searchParams.set('fit', 'standard');
        else url.searchParams.delete('fit');
        history.replaceState(history.state, '', url.toString());
      };

      // The form the line properties post with (the props' form= attribute),
      // looked up inside this picker's own section or dialog: the PDP and an
      // open quick-add dialog can carry the same form id. getAttribute, not
      // form.id: the form's own <input name="id"> shadows that property.
      const formIdInput = () => {
        const formId = propFit.getAttribute('form');
        if (!formId) return null;
        const scope = root.closest('dialog, .shopify-section') || document;
        const form = [...scope.querySelectorAll('form')].find((f) => f.getAttribute('id') === formId);
        return form ? form.querySelector('input[name="id"]') : null;
      };

      // THEODORE: (bug #456 review) the quick-add dialog's product form, made
      // fresh. Horizon's product-form-component creates its AbortController
      // once, as a field: disconnect aborts it, and a reconnect subscribes again
      // with the dead signal. quick-add.js morphs each product into the previous
      // one's dialog markup, and when the block sequences differ (a monogram
      // block, the spec selects) morph keeps the old buy-buttons and moves it,
      // which detaches and re-attaches the form. That form never hears another
      // variant change: its id input stays on the size the dialog opened on and
      // Add posts that (seen: Standard size 34 posted as 28; MTM chosen, 44
      // posted). A copy is a new element with live listeners, and cloning keeps
      // the inputs' current values. Once per dialog open, before anything here
      // changes the size. The root cause sits in product-form.js; this keeps a
      // fit-gated garment right whatever that file does.
      const dialog = root.closest('dialog');
      const reviveDialogForm = () => {
        const idInput = formIdInput();
        const form = idInput && idInput.closest('product-form-component');
        if (!dialog || !form || !dialog.contains(form)) return;
        form.replaceWith(form.cloneNode(true));
      };

      // Re-fires the checked size when the form's id input disagrees with it:
      // a variant change the form missed (quick-add's own sync or its cached
      // re-morph, before the revive above) is fetched again and heard.
      const followCheckedSize = () => {
        const checked = root.querySelector('variant-picker .variant-option input[type="radio"]:checked');
        const idInput = formIdInput();
        if (!checked || !checked.dataset.variantId || !idInput || !idInput.value) return;
        if (idInput.value !== checked.dataset.variantId) {
          checked.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      // The product page's sticky bar shows the variant title Horizon rendered
      // (Made to measure) until the fetch for the new size lands. On a one-option
      // product that title is the size, so write it now; Horizon's morph then
      // replaces it with the server's own.
      const stickyTitle = (ctl, value) => {
        const bar = root.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
        if (!bar || bar.dataset.productId !== ctl.picker.dataset.productId) return;
        const title = bar.querySelector('.sticky-add-to-cart__info[data-single-option="true"] .sticky-add-to-cart__variant');
        if (title) title.textContent = value;
      };

      let ready = false; // reconcile() may move the Size picker (see settle)
      let live = false; // variant-picker.js has run: a size pressed now is heard
      let reconciling = false;
      let lastReal = ''; // the last real size this picker had
      let loadedReal = ''; // the real size the page opened on (a ?variant= link)
      let restoredStandard = false; // Standard size reopened from fit=standard
      // Standard size was chosen before the picker could hear a change, or Add
      // was refused for a size before DOMContentLoaded, and no real size was on
      // screen: Add asks for a size until one is pressed.
      let sizeUnconfirmed = false;

      // THEODORE: (bug #456) idempotent: does nothing once the Size picker
      // matches the fit, so the change it dispatches (which bubbles back here
      // through update()) cannot loop. Runs at the START of sync(), which
      // update() and the submit-time checkFit() call, before the props are
      // computed from the size.
      const reconcile = (atConnect) => {
        // Not once detached: a late update() (the rAF after a size change, held
        // while the tab is not drawing) would otherwise write fit=standard, and
        // through Horizon ?variant=, onto the page the dialog was opened from.
        if (reconciling || !root.isConnected) return;
        const ctl = sizeControl();
        if (!ctl) return;
        const standard = root.querySelector('.td-fit__radio:checked')?.value === 'standard';
        // The marker is written even before the picker can hear a change: a
        // size chosen early is replayed at DOMContentLoaded, and Horizon builds
        // that replay's ?variant= from the address as it stands then.
        syncAddress(ctl, standard);
        if (!ready) return;
        const cur = ctl.current;
        if (cur && !isMtm(cur)) lastReal = cur.value;

        const findReal = (value) => (value ? ctl.real.find((o) => o.value === value) : undefined);
        let target = null;
        if (standard) {
          // Never blocks Add: the last real size, else the one the page opened
          // on, else the first (today's default, 44/28/37/S).
          // Not while the size is unconfirmed: a size drawn checked under
          // "Choose a size." reads as a contradiction.
          if ((!cur || isMtm(cur)) && !sizeUnconfirmed) {
            target =
              findReal(lastReal) ||
              findReal(loadedReal) ||
              ctl.real.find((o) => o.dataset.optionAvailable !== 'false' && !o.disabled) ||
              ctl.real[0] ||
              null;
          }
        } else if (!cur || !isMtm(cur)) {
          target = ctl.mtm;
        }
        // Once at connect, the radio on screen may not be what the form will
        // post: quick-add re-morphs its picker from cached HTML after this
        // element connects, and a reload can restore a radio but not the id.
        // Not when the picker has already heard that radio (it marks it
        // data-current-checked as it does): its fetch is in flight, and a
        // second change would abort it.
        if (!target && atConnect && cur && cur.dataset.variantId && cur.dataset.currentChecked !== 'true') {
          const idInput = formIdInput();
          if (idInput && idInput.value && idInput.value !== cur.dataset.variantId) target = cur;
        }
        if (!target) return;

        reconciling = true;
        try {
          if (!isMtm(target) && onProductPage(ctl)) stickyTitle(ctl, target.value);
          if (ctl.select) {
            ctl.select.value = target.value;
            ctl.select.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } finally {
          reconciling = false;
        }
      };

      const setProps = (fit, machine) => {
        propFit.value = fit || '';
        propMachine.value = machine || '';
        propFit.disabled = !fit;
        propMachine.disabled = !machine;
      };

      const sync = (when) => {
        reconcile(when === AT_CONNECT);
        root.querySelectorAll('.td-fit__row').forEach((row) => {
          const radio = row.querySelector('.td-fit__radio');
          const active = Boolean(radio && radio.checked);
          row.classList.toggle('is-active', active);
          const reveal = row.querySelector('.td-fit__reveal');
          if (reveal) reveal.inert = !active; // hidden from AT + tab order
          row.querySelectorAll('.td-fit__reveal select, .td-fit__reveal input, .td-fit__reveal button, .td-fit__reveal a').forEach((c) => {
            if (active) c.removeAttribute('tabindex');
            else c.setAttribute('tabindex', '-1');
          });
        });
        const selected = root.querySelector('.td-fit__radio:checked');
        // THEODORE: the standard-size detail selects only submit on the
        // standard path — disabled inputs never reach the cart.
        // A detail left on Standard is not sent at all: it carries its
        // property name (data-td-name) only while it holds a value, or the
        // cart line and the order would store it as an empty string.
        root.querySelectorAll('[data-td-specs] select').forEach((sel) => {
          // Markup rendered before data-td-name (a page cached or opened
          // before a publish) carries the name alone: keep it.
          if (!sel.dataset.tdName && sel.getAttribute('name')) sel.dataset.tdName = sel.getAttribute('name');
          const on = Boolean(selected && selected.value === 'standard');
          sel.disabled = !on;
          if (on && sel.value && sel.dataset.tdName) sel.setAttribute('name', sel.dataset.tdName);
          else sel.removeAttribute('name');
        });
        if (!selected) return setProps('', '');
        if (selected.value === 'profile') {
          const sel = q('.td-fit__profile-select');
          const opt = sel && sel.selectedOptions[0];
          if (opt) {
            setProps('Your measurements — ' + opt.textContent.trim(), 'profile:' + opt.value);
            const record = q('.td-fit__record');
            if (record) record.textContent = (opt.dataset.date ? 'Taken ' + opt.dataset.date : dateNotRecorded) + ' · held on file';
          } else setProps('', '');
        } else if (selected.value === 'standard') {
          const size = readStandardSize();
          size ? setProps('Standard size ' + size, 'standard:' + size) : setProps('', '');
        } else if (selected.value === 'appointment') {
          const when = root.dataset.apptDate;
          setProps('Measured at my fitting appointment' + (when ? ' — ' + when : ''), 'appointment');
        } else if (selected.value === 'code') {
          const code = (q('.td-fit__code-input').value || '').trim();
          const ok = root.dataset.codeOk === code && code;
          ok ? setProps('Measurement code ' + code, 'code:' + code) : setProps('', '');
        }
      };

      // ── A made-to-order garment goes in the bag with a fit ──
      // Add to cart is never disabled (a greyed button gives no reason); the
      // product form asks checkFit on submit and, when nothing usable is
      // chosen, the picker says what is missing and takes focus.
      const visibleRadios = () => [...root.querySelectorAll('.td-fit__radio')].filter((r) => !r.closest('[hidden]'));
      const fitProblem = () => {
        const selected = root.querySelector('.td-fit__radio:checked');
        // Standard size chosen before variant-picker.js ran (or refused before
        // DOMContentLoaded), with no size: the size reconcile() would pick was
        // never on screen when Add was pressed.
        if (sizeUnconfirmed && selected?.value === 'standard') {
          const ctl = sizeControl();
          const size = ctl && (ctl.select || ctl.real.find((o) => !o.disabled && o.dataset.optionAvailable !== 'false'));
          return { text: 'Choose a size.', target: size || q('.td-fit__size-select') || selected };
        }
        if (!propMachine.disabled && propMachine.value) return null;
        const first = visibleRadios()[0] || null;
        // Each line fits one line on a phone: it sits above the options and
        // follows the code field as it is typed in, so a line that rewrapped
        // between two of these moved the field under the customer's finger.
        const choose = { text: 'Choose how we size this garment.', target: first, group: true };
        if (!selected) return choose;
        if (selected.value === 'code') {
          const input = q('.td-fit__code-input');
          const code = (input?.value || '').trim();
          if (!code) return { text: 'Enter your code, then press Check.', target: input };
          // A code already checked and not found must not be told to press Check again.
          if (root.dataset.codeBad === code) return { text: 'Correct the code, or choose another option.', target: input };
          return { text: 'Press Check to confirm your code.', target: input };
        }
        if (selected.value === 'standard') {
          return { text: 'Choose a size.', target: q('.td-fit__size-select') || selected };
        }
        return { ...choose, target: selected };
      };
      let marked = [];
      const unmark = () => {
        root.classList.remove('is-missing');
        if (note) { note.textContent = ''; note.hidden = true; }
        marked.forEach((el) => { el.removeAttribute('aria-invalid'); el.removeAttribute('aria-describedby'); });
        marked = [];
      };
      const mark = (problem, move) => {
        unmark();
        root.classList.add('is-missing');
        if (note) { note.textContent = problem.text; note.hidden = false; }
        // The radios are marked only when no usable option is chosen; a code or
        // size left incomplete marks that field alone.
        marked = [...new Set([...(problem.group ? visibleRadios() : []), problem.target].filter(Boolean))];
        marked.forEach((el) => {
          el.setAttribute('aria-invalid', 'true');
          if (note) el.setAttribute('aria-describedby', note.id);
        });
        if (!move) return;
        // A picker already in full view stays where it is. Otherwise its top
        // goes to the top of the page, below the sticky header (the picker's
        // scroll margin); Chrome's 'nearest' ignores that margin while any of
        // the picker shows, and left it under the header on phones. In the
        // quick-add dialog nothing overlaps, so 'nearest' is enough there.
        // 'instant' because a smooth scroll would still be running at the focus.
        const scroll = (block) => {
          try { root.scrollIntoView({ block, behavior: 'instant' }); } catch { root.scrollIntoView(); }
        };
        if (root.closest('dialog')) scroll('nearest');
        else {
          const box = root.getBoundingClientRect();
          const clear = parseFloat(getComputedStyle(root).scrollMarginTop) || 0;
          if (box.top < clear || box.bottom > window.innerHeight) scroll('start');
        }
        const target = problem.target;
        if (target) {
          try { target.focus({ preventScroll: true }); } catch { target.focus(); }
        }
      };
      const update = (when) => {
        sync(when);
        // Once marked, the line follows the choice: reworded as it changes,
        // gone once the fit is complete.
        if (root.classList.contains('is-missing')) {
          const problem = fitProblem();
          problem ? mark(problem, false) : unmark();
        }
      };
      this.#check = (report) => {
        update();
        const problem = fitProblem();
        if (problem && report) mark(problem, true);
        // Standard size refused before DOMContentLoaded ('Choose a size.') on a
        // garment with Made to measure: the size settle() would pick there is not
        // picked for the customer. Only there: without that value settle() never
        // sets ready, and the flag would outlive the size the customer then chooses.
        if (problem && report && !ready && sizeControl() && root.querySelector('.td-fit__radio:checked')?.value === 'standard') {
          sizeUnconfirmed = true;
        }
        return !problem;
      };

      root.addEventListener('change', (e) => {
        // The code field's change comes on blur, often from the press on Check:
        // rewording the error line then moved Check from under the pointer and
        // the press was lost. Its input listener and the check itself update.
        if (e.target instanceof HTMLInputElement && e.target.classList.contains('td-fit__code-input')) return;
        if (sizeUnconfirmed && e.isTrusted && e.target instanceof HTMLSelectElement && e.target.closest('variant-picker') &&
            !isMtm(e.target.selectedOptions[0])) sizeUnconfirmed = false;
        if (e.target instanceof HTMLInputElement && e.target.classList.contains('td-fit__radio')) {
          const ctl = live ? null : sizeControl();
          sizeUnconfirmed = Boolean(ctl && e.target.value === 'standard' && (!ctl.current || isMtm(ctl.current)));
        }
        update();
        // Horizon morphs the picker in place after its fetch, which can land
        // after this handler — settle again once that has happened.
        if (e.target instanceof Element && e.target.closest('variant-picker')) {
          requestAnimationFrame(() => update());
        }
      });
      // A press on a real size, even the one already checked (no change event
      // then), confirms it.
      root.addEventListener('click', (e) => {
        const size = e.target instanceof Element && e.target.closest('variant-picker label, variant-picker input');
        const input = size && (size.matches('input') ? size : size.querySelector('input'));
        if (input && !isMtm(input) && sizeUnconfirmed) { sizeUnconfirmed = false; update(); }
      });
      const hasMtm = Boolean(sizeControl());
      if (hasMtm) {
        // THEODORE: (bug #456) fit=standard is only ever written by this
        // picker, when the customer chose Standard size here: reopen it (a
        // reload, or a Back the browser did not keep in its page cache). Done
        // at connect, before the signed-in /apps/fit request can answer, so
        // its auto-select always finds restoredStandard set and leaves it.
        const ctl = sizeControl();
        const std = q('.td-fit__row--standard .td-fit__radio');
        if (
          std &&
          onProductPage(ctl) &&
          !root.querySelector('.td-fit__radio:checked') &&
          new URL(window.location.href).searchParams.get('fit') === 'standard'
        ) {
          std.checked = true;
          restoredStandard = true;
        }
        // On a reload or an uncached Back, Chrome restored the fit radio (it
        // matches the per-instance name given above) well after settle and
        // pageshow, with no change event: the row showed a fit whose props were
        // never written. Not saved, not restored: the address (fit=standard)
        // is the one memory of the fit.
        root.querySelectorAll('.td-fit__radio').forEach((r) => r.setAttribute('autocomplete', 'off'));
        // Standard size pressed before this element upgraded, with no size:
        // that press fired no change here.
        const early = root.querySelector('.td-fit__radio:checked');
        if (!restoredStandard && early && early.value === 'standard' && (!ctl.current || isMtm(ctl.current))) {
          sizeUnconfirmed = true;
        }
      }
      update();

      // THEODORE: (bug #456) the first reconcile waits. In the quick-add dialog
      // this element connects (and upgrades) before its nested variant-picker
      // has attached its change listener, and quick-add.js then re-morphs that
      // picker from cached HTML; a macrotask after variant-picker is defined
      // runs after both, and after the dialog's morph has put the product form
      // in place, so that is also when the dialog's form is revived. On the PDP
      // it is what makes script order safe: this file runs ahead of
      // variant-picker.js there (snippets/scripts.liquid).
      if (hasMtm || dialog) {
        const settle = () => {
          if (!root.isConnected || ready) return;
          if (dialog) reviveDialogForm();
          const ctl = sizeControl();
          if (ctl) {
            if (ctl.current && !isMtm(ctl.current)) loadedReal = ctl.current.value;
            ready = true;
            update(AT_CONNECT);
          } else if (dialog) {
            // No MTM value: reconcile stays off, the form need only post the
            // size on screen.
            followCheckedSize();
          }
        };
        // On the product page not before DOMContentLoaded, so the size it may
        // fire reaches every block that follows the variant, and after
        // variant-picker's own replay of an early size, which it then finds
        // already heard. Until then reconcile() leaves the Size picker alone. A
        // press held by theodore-head.liquid can repeat before this runs:
        // product-form.js then posts the size on screen, or Made to measure for
        // a measured fit (window.__tdParsed: see variant-picker.js).
        customElements.whenDefined('variant-picker').then(() => {
          live = true;
          const navigation = performance.getEntriesByType?.('navigation')[0];
          const flag = window.__tdParsed;
          const parsed =
            document.readyState === 'complete' ||
            flag === true ||
            (navigation ? navigation.domContentLoadedEventStart > 0 : flag !== false && document.readyState !== 'loading');
          if (dialog || parsed) setTimeout(settle, 0);
          else document.addEventListener('DOMContentLoaded', settle, { once: true });
        });
      }

      // Sign-in dialog (logged-out only).
      const signin = q('.td-fit__signin');
      const modal = q('dialog.td-fit-modal');
      if (signin && modal) {
        signin.addEventListener('click', () => { if (!modal.open) modal.showModal(); });
        modal.addEventListener('click', (e) => {
          // Backdrop only — clicks on the card's own padding must not close.
          if (e.target !== modal) return;
          // THEODORE: inside the quick-add dialog this dialog is nested in another one,
          // which reads a click on a <dialog> outside its own box as "clicked outside"
          // and closes too. Dismissing the sign-in prompt must leave the garment open.
          e.stopPropagation();
          const r = modal.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
            modal.close();
          }
        });
        // THEODORE: likewise Escape — the quick-add dialog listens for the same key.
        modal.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') e.stopPropagation();
        });
      }

      const codeBtn = q('.td-fit__code-check');
      if (codeBtn) {
        const codeInput = q('.td-fit__code-input');
        const result = q('.td-fit__code-result');
        // Busy is aria-disabled, not disabled: disabling the focused button
        // threw a keyboard user's focus back to the top of the page. A press
        // while a check is out sends nothing; it is remembered with the code it
        // was made for, and acted on only if that code is still in the box and
        // is not the one being checked.
        let checking = false;
        let again = null;
        const check = async () => {
          const code = (codeInput.value || '').trim();
          if (!code) return;
          // Editing the field clears codeOk below. An unchanged verified code
          // does not need another lookup or a misleading new error beside it.
          if (root.dataset.codeOk === code) {
            result.textContent = 'Measurements found';
            result.classList.add('is-found');
            update();
            return;
          }
          if (checking) { again = code; return; }
          checking = true;
          codeBtn.setAttribute('aria-disabled', 'true');
          result.textContent = 'Checking…';
          result.classList.remove('is-found');
          let data = null;
          let rateLimited = false;
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 6000);
          try {
            // Direct bootstrap preserves the visitor's network identity at Vercel;
            // Shopify's proxy otherwise hides every visitor behind its own edge.
            const sessionResponse = await fetch('https://infinity.thdr.ai/api/proxy/fit/session', {
              headers: { Accept: 'application/json' }, signal: controller.signal,
              cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer',
            });
            if (!sessionResponse.ok) throw new Error('Measurement-code session unavailable');
            const session = await sessionResponse.json();
            if (typeof session?.token !== 'string' || !session.token) throw new Error('Measurement-code session unavailable');
            const res = await fetch('/apps/fit?code=' + encodeURIComponent(code) + '&fit_session=' + encodeURIComponent(session.token), {
              headers: { Accept: 'application/json' }, signal: controller.signal, cache: 'no-store',
            });
            rateLimited = res.status === 429;
            if (res.ok) {
              const body = await res.json();
              if (typeof body?.code_found === 'boolean') data = body;
            }
          } catch {
            // A failed/unfinished read is not proof that this code is absent.
          } finally {
            clearTimeout(timer);
          }
          checking = false;
          codeBtn.removeAttribute('aria-disabled');
          const pressed = again;
          again = null;
          const now = (codeInput.value || '').trim();
          // The answer is for a code no longer in the box: drop it.
          if (now !== code) {
            result.textContent = now ? 'Code changed — check it again.' : '';
            update();
            if (pressed === now) check();
            return;
          }
          if (!data) {
            result.textContent = rateLimited
              ? (root.dataset.codeRateLimited || 'Too many checks. Please wait 10 minutes, then try again.')
              : 'The check didn’t go through — try again in a moment.';
          } else if (data.code_found) {
            delete root.dataset.codeBad;
            root.dataset.codeOk = code;
            result.textContent = 'Measurements found';
            result.classList.add('is-found');
            // Select the code option, unless another fit was chosen while it was checked.
            const radio = root.querySelector('.td-fit__row--code .td-fit__radio');
            const chosen = root.querySelector('.td-fit__radio:checked');
            if (radio && (!chosen || chosen === radio)) radio.checked = true;
          } else {
            delete root.dataset.codeOk;
            root.dataset.codeBad = code;
            result.textContent = 'That code isn’t in our records. Correct it, or book a fitting.';
          }
          update();
        };
        codeBtn.addEventListener('click', check);
        codeInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); check(); }
        });
        // Editing the code after a successful check must visibly undo it —
        // never leave a stale tick beside a code we haven't verified.
        codeInput.addEventListener('input', () => {
          const now = (codeInput.value || '').trim();
          if (root.dataset.codeOk && root.dataset.codeOk !== now) {
            delete root.dataset.codeOk;
            result.textContent = 'Code changed — check it again.';
            result.classList.remove('is-found');
            update();
          } else if (root.dataset.codeBad && root.dataset.codeBad !== now) {
            // Nor a "not in our records" line beside a code that was never checked.
            delete root.dataset.codeBad;
            result.textContent = 'Code changed — check it again.';
            update();
          } else if (root.classList.contains('is-missing')) {
            // A refused Add's line follows the field as it is typed in (not on
            // blur, which comes from the press on Check: see the change listener).
            update();
          }
        });
      }

      if (loggedIn) {
        const loading = q('.td-fit__loading');
        const feedback = q('.td-fit__lookup');
        const status = q('.td-fit__lookup-status');
        const retry = q('.td-fit__retry');
        let controller = null;
        let timer = null;
        let loaded = false;
        const cancel = () => {
          const previous = controller;
          controller = null;
          clearTimeout(timer);
          previous?.abort();
        };
        this.#cancelLookup = cancel;
        const load = async () => {
          // Retry is a deliberate press, never a second concurrent request.
          if (controller || loaded) return;
          const current = new AbortController();
          controller = current;
          timer = setTimeout(() => current.abort(), 6000);
          if (retry) {
            retry.setAttribute('aria-disabled', 'true');
            retry.setAttribute('aria-busy', 'true');
          }
          if (status && feedback) status.textContent = feedback.dataset.retrying;
          try {
            const response = await fetch('/apps/fit', { headers: { Accept: 'application/json' }, signal: current.signal });
            if (!response.ok) throw new Error(String(response.status));
            const data = await response.json();
            if (!data || typeof data !== 'object' || (!Array.isArray(data.profiles) && data.logged_in !== false)) {
              throw new Error('Invalid fitting details');
            }
            if (controller !== current || !root.isConnected) return;
            profiles = Array.isArray(data.profiles) ? data.profiles : [];

            if (profiles.length) {
              const row = q('.td-fit__row--measurements');
              const sel = q('.td-fit__profile-select');
              const selected = sel.value;
              sel.replaceChildren();
              profiles.forEach((p) => {
                const opt = document.createElement('option');
                opt.value = p.id;
                opt.dataset.date = fmtDate(p.taken_at);
                opt.textContent = p.label + ' · ' + (opt.dataset.date || dateNotRecorded);
                sel.appendChild(opt);
              });
              if (profiles.some((p) => p.id === selected)) sel.value = selected;
              row.hidden = false;
              // A saved choice is a default only while the shopper has not chosen.
              // The request can finish after a standard size or appointment was selected.
              if (!root.querySelector('.td-fit__radio:checked')) row.querySelector('.td-fit__radio').checked = true;
            }

            if (data.upcoming_appointment) {
              const when = fmtDate(data.upcoming_appointment.start_at);
              root.dataset.apptDate = when;
              const sub = q('.td-fit__appt-sub');
              if (sub) sub.textContent = 'Booked — ' + when + '.';
              const note = q('.td-fit__appt-note');
              if (note) note.hidden = true;
              if (!profiles.length && !root.querySelector('.td-fit__radio:checked')) {
                q('.td-fit__row--appointment .td-fit__radio').checked = true;
              }
            }

            if (Array.isArray(data.standard_sizes) && data.standard_sizes.length) {
              const sizeSel = q('.td-fit__size-select');
              if (sizeSel) {
                const current = sizeSel.value;
                sizeSel.length = 1;
                data.standard_sizes.forEach((s) => {
                  const opt = document.createElement('option');
                  opt.value = s;
                  opt.textContent = s;
                  sizeSel.appendChild(opt);
                });
                sizeSel.value = current;
              }
            }
            // book_url from the proxy is ignored: the block setting owns the booking link.
            update();
            loaded = true;
            const retryHadFocus = retry && document.activeElement === retry;
            if (feedback) feedback.hidden = true;
            // A successful retry removes its button. Keep keyboard focus on
            // the fit the customer chose, or the first available option.
            if (retryHadFocus) {
              const selected = q('.td-fit__radio:checked');
              (selected || q('.td-fit__row:not([hidden]) .td-fit__radio'))?.focus();
            }
          } catch {
            if (controller !== current || !root.isConnected) return;
            if (status && feedback) {
              status.textContent = feedback.dataset.unavailable;
              feedback.hidden = false;
            }
          } finally {
            if (controller === current) {
              clearTimeout(timer);
              controller = null;
              if (loading) loading.remove();
              retry?.removeAttribute('aria-disabled');
              retry?.removeAttribute('aria-busy');
            }
          }
        };
        retry?.addEventListener('click', load);
        this.#resumeLookup = load;
        load();
      }
    }
  }

  customElements.define('theodore-fit-picker', TheodoreFitPicker);
})();
